// ═══════════════════════════════════════════════════════════════
// PACE — Directory
// View all PACE profiles. Admins can click in and edit any record.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Box, Title, Text, Group, Stack, Paper, Table, Badge, TextInput,
  Select, SimpleGrid, Modal, Center, Loader, Progress, Button, Textarea,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconListCheck, IconCheck, IconEdit, IconDeviceFloppy, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { usePaceStore, PACE_TIERS, PACE_TIER_LABELS } from '../../store/paceStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { PACEProfile } from '../../store/paceStore';

const TIER_FIELDS: Record<string, keyof PACEProfile> = {
  primary: 'primary',
  alternate: 'alternate',
  contingency: 'contingency',
  emergency: 'emergency',
};

const TIER_COLORS: Record<string, string> = {
  primary: 'green',
  alternate: 'blue',
  contingency: 'yellow',
  emergency: 'red',
};

const TIER_PLACEHOLDERS: Record<string, string> = {
  primary: 'e.g. Cell: (404) 555-0100',
  alternate: 'e.g. Radio: W4EMC 147.195 MHz',
  contingency: 'e.g. Satellite: Iridium Unit 3',
  emergency: 'e.g. In-Person: EOC Lobby',
};

function tierFilled(profile: PACEProfile, tier: string): boolean {
  const key = TIER_FIELDS[tier] as keyof PACEProfile;
  return !!(profile[key] as string)?.trim();
}

function filledCount(profile: PACEProfile): number {
  return PACE_TIERS.filter(t => tierFilled(profile, t)).length;
}

type EnrichedProfile = PACEProfile & { filled: number; isComplete: boolean };

interface EditForm {
  profileName: string;
  primary: string;
  alternate: string;
  contingency: string;
  emergency: string;
  status: string;
  notes: string;
}

function ProfileModal({ profile, canEdit, onSaved }: {
  profile: EnrichedProfile;
  canEdit: boolean;
  onClose: () => void;
  onSaved: (updated: Partial<PACEProfile>) => void;
}) {
  const { updateProfile } = usePaceStore();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EditForm>({
    profileName: profile.profileName,
    primary: profile.primary,
    alternate: profile.alternate,
    contingency: profile.contingency,
    emergency: profile.emergency,
    status: profile.status || 'active',
    notes: profile.notes,
  });

  const handleSave = async () => {
    if (!form.profileName.trim()) {
      notifications.show({ message: 'Profile name is required', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      await updateProfile(profile.id, form);
      notifications.show({ message: 'Profile saved', color: 'green' });
      onSaved(form);
      setEditing(false);
    } catch {
      notifications.show({ message: 'Failed to save', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      profileName: profile.profileName,
      primary: profile.primary,
      alternate: profile.alternate,
      contingency: profile.contingency,
      emergency: profile.emergency,
      status: profile.status || 'active',
      notes: profile.notes,
    });
    setEditing(false);
  };

  const displayProfile = editing ? { ...profile, ...form } : profile;

  return (
    <Stack gap="md">
      {/* Header actions */}
      <Group justify="space-between" align="flex-start">
        <Group gap="xs" wrap="wrap">
          <Badge size="sm" variant="light" color={displayProfile.status === 'active' ? 'green' : 'gray'}>
            {displayProfile.status || 'active'}
          </Badge>
          <Badge size="sm" variant="light" color={displayProfile.isComplete ? 'green' : 'yellow'}>
            {filledCount(displayProfile as PACEProfile)}/4 tiers filled
          </Badge>
        </Group>
        {canEdit && !editing && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconEdit size={13} />}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
        {editing && (
          <Group gap="xs">
            <Button size="xs" variant="subtle" color="gray" leftSection={<IconX size={13} />} onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="xs" leftSection={<IconDeviceFloppy size={13} />} loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Group>
        )}
      </Group>

      {/* Profile name */}
      {editing ? (
        <TextInput
          label="Profile Name"
          value={form.profileName}
          onChange={e => setForm(f => ({ ...f, profileName: e.target.value }))}
          required
        />
      ) : null}

      {/* PACE Tiers */}
      <Box>
        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="sm">PACE Tiers</Text>
        <Stack gap="xs">
          {PACE_TIERS.map(tier => {
            const value = editing
              ? (form[tier as keyof EditForm] as string)
              : ((displayProfile[TIER_FIELDS[tier] as keyof PACEProfile] as string) || '');
            const filled = !!value.trim();
            const color = TIER_COLORS[tier];
            return (
              <Paper
                key={tier}
                p="sm"
                withBorder
                style={{
                  background: filled ? `var(--mantine-color-${color}-0)` : 'var(--mantine-color-gray-0)',
                  borderColor: filled ? `var(--mantine-color-${color}-3)` : 'var(--mantine-color-gray-3)',
                }}
              >
                <Group gap={8} mb={editing || filled ? 6 : 0}>
                  <Box
                    w={8} h={8}
                    style={{
                      borderRadius: '50%',
                      background: filled ? `var(--mantine-color-${color}-5)` : 'var(--mantine-color-gray-4)',
                      flexShrink: 0,
                    }}
                  />
                  <Badge color={filled ? color : 'gray'} variant="light" size="sm">
                    {PACE_TIER_LABELS[tier]}
                  </Badge>
                </Group>
                {editing ? (
                  <Textarea
                    placeholder={TIER_PLACEHOLDERS[tier]}
                    value={form[tier as keyof EditForm] as string}
                    onChange={e => setForm(f => ({ ...f, [tier]: e.target.value }))}
                    minRows={2}
                    autosize
                    styles={{ input: { fontSize: 13 } }}
                  />
                ) : filled ? (
                  <Text size="sm" pl={16} style={{ whiteSpace: 'pre-wrap' }}>{value}</Text>
                ) : (
                  <Text size="xs" c="dimmed" fs="italic" pl={16}>Not filled in</Text>
                )}
              </Paper>
            );
          })}
        </Stack>
      </Box>

      {/* Status selector (edit mode) */}
      {editing && (
        <Select
          label="Status"
          data={[
            { value: 'active', label: 'Active' },
            { value: 'draft', label: 'Draft' },
            { value: 'archived', label: 'Archived' },
          ]}
          value={form.status}
          onChange={val => setForm(f => ({ ...f, status: val || 'active' }))}
        />
      )}

      {/* Notes */}
      {editing ? (
        <Textarea
          label="Notes"
          placeholder="Any additional context..."
          value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          minRows={2}
          autosize
        />
      ) : displayProfile.notes ? (
        <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
          <Text size="xs" c="dimmed" fw={500} mb={4}>Notes</Text>
          <Text size="sm">{displayProfile.notes}</Text>
        </Paper>
      ) : null}
    </Stack>
  );
}

export default function Directory() {
  const { profiles, loading, loadProfiles } = usePaceStore();
  const { canEdit } = useModulePermission('pace');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected] = useState<EnrichedProfile | null>(null);
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);

  useEffect(() => { loadProfiles(); }, []);

  const enriched = useMemo(() => profiles.map(p => ({
    ...p,
    filled: filledCount(p),
    isComplete: filledCount(p) === 4,
  })), [profiles]);

  const filtered = useMemo(() => {
    setPage(1);
    return enriched.filter(p => {
      if (filterStatus === 'complete' && !p.isComplete) return false;
      if (filterStatus === 'incomplete' && p.isComplete) return false;
      if (filterStatus === 'active' && p.status !== 'active') return false;
      if (search) {
        const q = search.toLowerCase();
        return p.profileName.toLowerCase().includes(q) || p.notes.toLowerCase().includes(q);
      }
      return true;
    });
  }, [enriched, search, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = useMemo(() => ({
    total: enriched.length,
    complete: enriched.filter(p => p.isComplete).length,
    partial: enriched.filter(p => p.filled > 0 && !p.isComplete).length,
    empty: enriched.filter(p => p.filled === 0).length,
  }), [enriched]);

  const compliancePct = stats.total > 0 ? Math.round((stats.complete / stats.total) * 100) : 0;

  // Update selected profile in-place after save so modal reflects new data immediately
  const handleSaved = (updated: Partial<PACEProfile>) => {
    if (!selected) return;
    const merged = { ...selected, ...updated };
    const filled = filledCount(merged as PACEProfile);
    setSelected({ ...merged, filled, isComplete: filled === 4 });
  };

  if (loading && profiles.length === 0) {
    return <Center py="xl" mih={256}><Loader /></Center>;
  }

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={3} c="black">PACE Directory</Title>
          <Text c="dimmed" size="sm" mt={4}>
            {stats.total} personnel · {stats.complete} fully compliant · {compliancePct}% coverage
          </Text>
        </Box>
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        {[
          { label: 'Total Personnel', value: stats.total, color: 'gray' },
          { label: 'Complete (4/4)', value: stats.complete, color: 'green' },
          { label: 'Partial', value: stats.partial, color: 'yellow' },
          { label: 'Empty', value: stats.empty, color: 'red' },
        ].map(s => (
          <Paper key={s.label} p="sm" withBorder ta="center" style={{ borderTop: `3px solid var(--mantine-color-${s.color}-5)` }}>
            <Text size="xs" fw={500} tt="uppercase" c="dimmed">{s.label}</Text>
            <Text fz={28} fw={700} mt={2} c={s.color === 'gray' ? undefined : s.color}>{s.value}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      <Paper p="md" withBorder mb="md">
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={500}>Overall Coverage</Text>
          <Text size="sm" c="dimmed">{stats.complete} / {stats.total} ({compliancePct}%)</Text>
        </Group>
        <Progress value={compliancePct} color="green" size="sm" radius="xl" />
      </Paper>

      <Group gap="sm" mb="md" wrap="wrap">
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search by name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select
          data={[
            { value: 'all', label: 'All Personnel' },
            { value: 'complete', label: 'Complete (4/4)' },
            { value: 'incomplete', label: 'Incomplete' },
            { value: 'active', label: 'Active Only' },
          ]}
          value={filterStatus}
          onChange={val => setFilterStatus(val || 'all')}
          w={180}
        />
      </Group>

      {filtered.length === 0 ? (
        <Center py="xl" mih={200}>
          <Stack align="center" gap="xs">
            <IconListCheck size={40} color="#d1d5db" style={{ opacity: 0.3 }} />
            <Text fw={500} c="dimmed">
              {enriched.length === 0 ? 'No PACE profiles yet' : 'No profiles match filters'}
            </Text>
            <Text size="sm" c="dimmed">
              {enriched.length === 0
                ? 'Add profiles to see them here.'
                : 'Try adjusting your search or filter.'}
            </Text>
          </Stack>
        </Center>
      ) : (
        <Paper withBorder style={{ overflow: 'hidden' }}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name / Role</Table.Th>
                <Table.Th>PACE Coverage</Table.Th>
                <Table.Th visibleFrom="sm">Status</Table.Th>
                <Table.Th visibleFrom="md">Completeness</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map(profile => (
                <Table.Tr
                  key={profile.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelected(profile)}
                >
                  <Table.Td>
                    <Text size="sm" fw={500}>{profile.profileName || '(Unnamed)'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {PACE_TIERS.map(t => (
                        <Box
                          key={t}
                          title={PACE_TIER_LABELS[t]}
                          w={6}
                          h={16}
                          style={{
                            borderRadius: 2,
                            background: tierFilled(profile, t)
                              ? `var(--mantine-color-${TIER_COLORS[t]}-5)`
                              : 'var(--mantine-color-gray-2)',
                          }}
                        />
                      ))}
                      <Text size="xs" c="dimmed">{profile.filled}/4</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td visibleFrom="sm">
                    <Badge size="sm" variant="light" color={profile.status === 'active' ? 'green' : 'gray'}>
                      {profile.status || 'active'}
                    </Badge>
                  </Table.Td>
                  <Table.Td visibleFrom="md">
                    {profile.isComplete ? (
                      <Badge color="green" variant="light" size="sm" leftSection={<IconCheck size={10} />}>
                        Complete
                      </Badge>
                    ) : profile.filled > 0 ? (
                      <Badge color="yellow" variant="light" size="sm">
                        Partial
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" size="sm">
                        Empty
                      </Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Box px="md" py="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-2)', background: 'var(--mantine-color-gray-0)' }}>
            <Text size="xs" c="dimmed">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} personnel
              {canEdit && ' · Click any row to view or edit'}
            </Text>
          </Box>
        </Paper>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      <Modal
        opened={!!selected}
        onClose={() => setSelected(null)}
        title={
          selected ? (
            <Group gap="xs" align="center">
              <Text fw={600}>{selected.profileName || '(Unnamed)'}</Text>
            </Group>
          ) : null
        }
        size="lg"
        styles={{ body: { paddingTop: 8 } }}
      >
        {selected && (
          <ProfileModal
            profile={selected}
            canEdit={canEdit}
            onClose={() => setSelected(null)}
            onSaved={handleSaved}
          />
        )}
      </Modal>
    </Box>
  );
}
